import { SUPABASE_URL } from '../../../utils/supabase/url';
import { isProjectPipelineStatus, type ProjectPipelineStatus } from '../../../types/projectPipelineStatus';
import { optimisticEntityWrite, type OptimisticCache } from '../optimisticWrite';
import type {
  Database,
  Company,
  User,
  Project,
  ContractItem,
  InterimApplication,
  Tender,
  RetentionLedger,
  Task,
  TaskComment,
  Notification,
  ShareToken,
  RateLibrary,
  MainContractor,
  Contact,
  EstimatingTeamMember,
  Supplier,
  TermsTemplate,
  RateLibraryItem,
  RICSBenchmark,
  CommercialHealthMetric,
  Employer,
  ClientEmployerRelationship,
  EntitlementAlert,
  ActiveDispute,
  ClientRequest,
  PendingDocumentUpload,
} from '../../../types/database';
import type { Claim, ClaimCorrespondence, ClaimSummaryMetrics, ClaimFilters } from '../../../types/claims';
import type { Document } from '../../../types/documents';
import type {
  Activity,
  ActivityFilter,
  ActivityStats,
  CreatePhoneCallData,
  CreateMeetingData,
  CreateEmailData,
  CreateNoteData,
  CreateCommentData,
} from '../../../types/activity';
import { ProjectFoldersService } from '../../ProjectFoldersService';
import { projectId } from '../../../utils/supabase/info';
import { geocodeAddress } from '../../../utils/geocoding';
import { buildProjectPathById } from '../../../utils/prettyRouteBuilders';
import { toast } from 'sonner';
import type { Constructor } from './mixins';
import { resolveProjectWorkspaceId } from '../projectWorkspace';
import { API_BASE, getAuthHeaders, getSupabaseClient } from '../../../utils/supabase/client';

const projectSettingsSaveQueues = new Map<string, Promise<unknown>>();
// A new project is in the local store before its row exists on the server.
// Access warmed in that window comes back empty and is cached as a confirmed
// answer, which shows the creator "Request access" on their own project.
const projectsAwaitingSave = new Set<string>();

export const withProjects = <TBase extends Constructor<any>>(Base: TBase) => class Projects extends Base {
  // PROJECTS
  // ============================================================================

  public getProjects(): Project[] {
    const projects = Object.values(this.db.projects) as Project[];
    if (projects.every((project: Project) => Boolean(project.stage))) {
      return projects;
    }

    return projects.map((project: Project) => {
      if (project.stage) return project;
      return {
        ...project,
        stage: 'tender_in_progress',
      } as unknown as Project;
    });
  }

  public getProject(id: string): Project | undefined {
    const project = this.db.projects[id];
    if (!project) return undefined;
    // Ensure project has a stage field
    return {
      ...project,
      stage: project.stage || 'tender_in_progress',
    };
  }

  public updateProjectSettingsAsync(
    id: string,
    update: (current: Project['settings']) => Project['settings'],
  ): Promise<Project | undefined> {
    const previous = projectSettingsSaveQueues.get(id) || Promise.resolve();
    const operation = previous
      .catch(() => undefined)
      .then(async () => {
        const project = this.db.projects[id] as Project | undefined;
        if (!project) return undefined;

        const nextSettings = update(project.settings || {});
        const procurement = nextSettings?.procurement;
        if (!procurement || typeof procurement !== 'object' || Array.isArray(procurement)) {
          throw new Error('Procurement settings must be an object');
        }

        // This method is currently used only for procurement. Persist that
        // subtree through the pricing-checked RPC, never a privileged update
        // of the complete project/settings row.
        const { data: saved, error } = await getSupabaseClient().rpc(
          'set_project_procurement_settings' as never,
          { p_project_id: id, p_state: procurement } as never,
        );
        if (error) throw error;

        const updated: Project = {
          ...project,
          settings: {
            ...(project.settings || {}),
            procurement: saved as typeof procurement,
          },
          updatedAt: new Date().toISOString(),
        };

        this.db.projects[id] = updated;
        this.saveToLocalStorage();
        this.notifyListeners();
        return updated;
      });

    projectSettingsSaveQueues.set(id, operation);
    void operation.then(
      () => {
        if (projectSettingsSaveQueues.get(id) === operation) {
          projectSettingsSaveQueues.delete(id);
        }
      },
      () => {
        if (projectSettingsSaveQueues.get(id) === operation) {
          projectSettingsSaveQueues.delete(id);
        }
      },
    );
    return operation;
  }

  /** True from a project's local creation until its server insert settles. */
  public isProjectAwaitingSave(id: string): boolean {
    return projectsAwaitingSave.has(id);
  }

  public getProjectsByClient(clientId: string): Project[] {
    const allProjects = this.getProjects();
    const filtered = allProjects.filter(p => p.clientId === clientId);
    return filtered;
  }

  public getProjectsByStaff(staffId: string): Project[] {
    return this.getProjects().filter(p => p.assignedStaffIds.includes(staffId));
  }

  public createProject(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Project {
    const { project: newProject, saved } = this._createProjectInternal(project);
    // Match historical fire-and-forget behaviour for callers that don't care
    // whether the backend save actually succeeded. createProjectAsync exposes
    // the promise to callers that do.
    saved.catch(() => { /* already logged in the internal chain */ });
    return newProject;
  }

  /**
   * Like createProject, but only resolves once the backend save has actually
   * succeeded. Rejects with the underlying save error if it didn't — which is
   * the signal forms need to avoid showing a false "Project created" toast.
   *
   * The optimistic local insert and the post-save side-effects (activity log,
   * workflow trigger, folder creation) still run exactly as before — only the
   * caller's resolution is gated on the projects-table POST landing.
   */
  public async createProjectAsync(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<Project> {
    const { project: newProject, saved } = this._createProjectInternal(project);
    await saved;
    return newProject;
  }

  private _createProjectInternal(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): { project: Project; saved: Promise<void> } {
    // Defensive guard: a now-removed code path used to persist names like
    // "Turton Road - <uuid>" by appending the row's own id to the display
    // name. The data was cleaned manually; this strips a trailing
    // " - <uuid|ts-rand>" before insert so any straggling caller can't
    // re-introduce the dirty state.
    if (project.name) {
      project = {
        ...project,
        name: project.name
          .replace(/\s*-\s*([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}|[0-9]{13}-[a-z0-9]{6,})$/i, '')
          .trim(),
      };
    }

    // Validate: duplicate = same name + same client + same main contractor
    const existingProjects = this.getProjectsByClient(project.clientId);
    const duplicateProject = existingProjects.find(p => {
      const sameName = p.name?.toLowerCase().trim() === project.name?.toLowerCase().trim();
      if (!sameName) return false;
      // Allow same name if it's for a different employer / main contractor
      const sameContractor =
        (p.mainContractorId && project.mainContractorId)
          ? p.mainContractorId === project.mainContractorId
          : p.mainContractor?.toLowerCase().trim() === project.mainContractor?.toLowerCase().trim();
      return sameContractor;
    });

    if (duplicateProject) {
      throw new Error(
        `⚠️ A project named "${project.name}" already exists for ${project.mainContractor || 'this employer'}. ` +
        `Please use a different name or update the existing project (ID: ${duplicateProject.id}).`
      );
    }
    
    const newProject: Project = {
      ...project,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      // Ensure stage is always set
      stage: project.stage || 'tender_in_progress',
    };
    this.db.projects[newProject.id] = newProject;
    this.saveToLocalStorage();
    
    // Async post-create work — three independent strands kicked off in
    // parallel so the slowest (folder creation) doesn't wait on the
    // others:
    //   1. Geocode the site address (cheap, but external — don't block save).
    //   2. Persist the project to the backend.
    //   3. Create the Google Drive folder structure once the project row
    //      exists in the DB (the folder route reads the project row when
    //      projectName / clientId aren't supplied; we always supply them
    //      now, but logging activity still depends on the row existing).
    //
    // The folder creation here is a backstop — the AddProjectForm also
    // calls ProjectFoldersService directly so it can attach a
    // toast.promise. The Edge Function's single-flight map dedupes both
    // calls into one Drive run.

    // Strand 1 — geocode in the background; updates the cached project
    // when complete. Doesn't gate anything else.
    if ((newProject.siteAddress || newProject.location) && (!newProject.latitude || !newProject.longitude)) {
      const addressToGeocode = newProject.siteAddress || newProject.location || '';
      geocodeAddress(addressToGeocode)
        .then((geoResult) => {
          if (!geoResult) return;
          const cached = this.db.projects[newProject.id];
          if (!cached) return;
          cached.latitude = geoResult.latitude;
          cached.longitude = geoResult.longitude;
          this.saveToLocalStorage();
          this.notifyListeners();
          // Persist the new coords to the backend without triggering the
          // full insert path again.
          void this.saveEntityToBackend('projects', cached.id, cached, false);
        })
        .catch((error) => {
          console.error('⚠️ Geocoding failed during project creation:', error);
        });
    }

    // Strand 2 + 3 — save then folder create.
    // The save promise is also returned from this helper so callers that opt
    // into createProjectAsync can await the actual backend write. The
    // post-save side-effects below stay fire-and-forget on a second .then
    // subscription so a caller declining to await doesn't leave an unhandled
    // rejection and a caller that does await still gets the original error.

    // Guard: if Supabase was disabled mid-session (hard network failure), reject
    // immediately so createProjectAsync surfaces the error rather than silently
    // resolving with nothing sent to the backend.
    if (!this.useSupabase) {
      const savedPromise = Promise.reject(new Error(
        'Backend sync is disabled in this session (likely after an earlier hard network failure). Project was not saved to the server.'
      ));
      this.notifyListeners();
      return { project: newProject, saved: savedPromise };
    }

    projectsAwaitingSave.add(newProject.id);
    const savedPromise = this.saveEntityToBackend('projects', newProject.id, newProject, true);
    // Listeners re-run once the row exists, so access is warmed against it.
    void savedPromise.finally(() => {
      projectsAwaitingSave.delete(newProject.id);
      this.notifyListeners();
    }).catch(() => {});
    savedPromise.then(async () => {
      // Log system activity for project creation
      const creator = this.getUser(project.createdBy || '');
      const client = this.getCompany(project.clientId);

      this.logActivity({
        type: 'project_created',
        category: 'project',
        action: 'New project created',
        detail: `${newProject.name} for ${client?.name || 'Unknown'}`,
        userId: creator?.id || project.createdBy || 'system',
        userName: creator?.name || 'System',
        userEmail: creator?.email || 'system@contractgroup.co.uk',
        metadata: {
          projectId: newProject.id,
          projectName: newProject.name,
          projectNumber: newProject.projectNumber,
          clientId: project.clientId,
          clientName: client?.name,
          stage: newProject.stage,
          contractValue: newProject.contractValue,
        },
      });

      // Project-created automation is enqueued atomically by the database.

      // Folder creation: don't gate on `getUser` succeeding — `createdBy`
      // is the source of truth and is always populated by the form.
      const ownerId = creator?.id || project.createdBy;
      if (!ownerId) {
        console.error('⚠️ Skipping folder creation — no createdBy on project');
        return;
      }
      try {
        const foldersService = new ProjectFoldersService();
        const result = await foldersService.createProjectFolders(
          newProject.id,
          newProject.name,
          ownerId,
          newProject.clientId,
        );
        if (result.success && !result.alreadyExists) {
          console.log(`📁 Created folder structure for project: ${newProject.name}`);
        }
      } catch (error) {
        console.error('❌ Error creating project folders:', error);
      }
    }).catch((err: unknown) => {
      // Logged here so the fire-and-forget post-save chain never surfaces as
      // an unhandled rejection. The awaitable savedPromise above still
      // carries the original error to callers that opted into
      // createProjectAsync.
      console.error('❌ Error saving project to backend:', err);
    });

    this.notifyListeners();
    return { project: newProject, saved: savedPromise };
  }

  public updateProject(id: string, updates: Partial<Project>): Project | undefined {
    const project = this.db.projects[id];
    if (!project) return undefined;
    
    // Store original project state for potential rollback
    const originalProject = { ...project };
    
    // Convert stage from frontend format to backend format before saving
    const processedUpdates: Partial<Project> & Record<string, any> = { ...updates };
    if (processedUpdates.stage) {
      const originalStage = processedUpdates.stage;
      processedUpdates.stage = this.convertStageToBackend(processedUpdates.stage as string) as any;
      
      // CRITICAL FIX: Also update legacy/backend field names to ensure transformProjectForBackend uses the new value
      // The transformer prioritizes project_stage/projectStage over stage, so we must update them too
      processedUpdates.project_stage = processedUpdates.stage;
      processedUpdates.projectStage = processedUpdates.stage;
    }
    if (processedUpdates.project_stage) {
      processedUpdates.project_stage = this.convertStageToBackend(processedUpdates.project_stage as string) as any;
      // Ensure sync reverse
      if (!processedUpdates.stage) processedUpdates.stage = processedUpdates.project_stage;
    }

    // Contractor reassignment is also a tenant transfer. Clear the previous
    // workspace mirror and resolve the receiving contractor's workspace in the
    // same optimistic update so local filters cannot retain the old tenant.
    if (processedUpdates.clientId !== undefined && processedUpdates.clientId !== project.clientId) {
      const nextClient = this.getCompany(processedUpdates.clientId);
      const nextWorkspaceId = resolveProjectWorkspaceId(
        processedUpdates.clientId,
        this.db.workspaces,
        nextClient?.workspaceId,
      );
      processedUpdates.workspaceId = nextWorkspaceId;
      processedUpdates.workspace_id = nextWorkspaceId;
    }
    
    // Handle supervisor assignment changes - sync assignedProjects on supervisor users
    if (updates.assignedSupervisorIds !== undefined) {
      const oldSupervisorIds = project.assignedSupervisorIds || [];
      const newSupervisorIds = updates.assignedSupervisorIds || [];
      
      // Find supervisors that were added
      const addedSupervisors = newSupervisorIds.filter(sid => !oldSupervisorIds.includes(sid));
      
      // Find supervisors that were removed
      const removedSupervisors = oldSupervisorIds.filter((sid: string) => !newSupervisorIds.includes(sid));
      
      // Update each added supervisor's assignedProjects array
      addedSupervisors.forEach(supervisorId => {
        const supervisor = this.db.users[supervisorId];
        if (supervisor) {
          const currentProjects = supervisor.assignedProjects || [];
          if (!currentProjects.includes(id)) {
            // Update supervisor's assigned projects
            const updatedSupervisor = {
              ...supervisor,
              assignedProjects: [...currentProjects, id],
              updatedAt: new Date().toISOString()
            };
            this.db.users[supervisorId] = updatedSupervisor;
            // Save to backend asynchronously (don't wait)
            this.saveEntityToBackend('users', supervisorId, updatedSupervisor, false);
            console.log(`✅ Added project ${id} to supervisor ${supervisor.name}'s assignedProjects`);
          }
        }
      });
      
      // Update each removed supervisor's assignedProjects array
      removedSupervisors.forEach((supervisorId: string) => {
        const supervisor = this.db.users[supervisorId];
        if (supervisor) {
          const currentProjects = supervisor.assignedProjects || [];
          // Update supervisor's assigned projects
          const updatedSupervisor = {
            ...supervisor,
            assignedProjects: currentProjects.filter((pid: string) => pid !== id),
            updatedAt: new Date().toISOString()
          };
          this.db.users[supervisorId] = updatedSupervisor;
          // Save to backend asynchronously (don't wait)
          this.saveEntityToBackend('users', supervisorId, updatedSupervisor, false);
          console.log(`✅ Removed project ${id} from supervisor ${supervisor.name}'s assignedProjects`);
        }
      });
    }
    
    // OPTIMISTIC UPDATE: Update local state immediately
    const updated = {
      ...project,
      ...processedUpdates,
      id,
      updatedAt: new Date().toISOString(),
    };
    
    // TRACKER: Log the update attempt
    
    this.db.projects[id] = updated;
    this.saveToLocalStorage();
    
    // Notify listeners immediately so UI updates right away
    this.notifyListeners();
    
    // Save to backend asynchronously with rollback on failure
    // Send only the changed fields. Sending the cached whole project would
    // turn a permitted financial edit into an attempted team/settings edit,
    // and could replay stale values after a permission change.
    (async () => {
      const response = await fetch(`${API_BASE}/projects/${id}`, {
        method: 'PUT',
        headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(processedUpdates),
      });
      if (!response.ok) throw new Error(`Project update failed (${response.status})`);
    })()
      .then(() => {
      })
      .catch((_error: unknown) => {

        // ROLLBACK: Restore original project state
        this.db.projects[id] = originalProject;
        this.saveToLocalStorage();
        this.notifyListeners();
        
        // Show error notification to user
        toast.error('Failed to save project changes', {
          description: 'Your changes could not be saved. Please try again.',
        });
      });
    
    // Log system activity for status/stage changes
    const stageChanged = (updates.stage && updates.stage !== project.stage) ||
                        ((updates as any).project_stage && (updates as any).project_stage !== (project as any).project_stage);

    if (stageChanged) {
      const oldStage = project.stage || (project as any).project_stage;
      const newStage = updates.stage || (updates as any).project_stage;
      const updater = updates.createdBy ? this.getUser(updates.createdBy) : null;
      const currentUser = this.getUsers().find((u: User) => u.email === 'gary@contractgroup.co.uk');
      const client = this.getCompany(project.clientId);
      
      this.logActivity({
        type: 'project_status_changed',
        category: 'project',
        action: 'Project stage updated',
        detail: `${updated.name}: ${oldStage} → ${newStage}`,
        userId: updater?.id || currentUser?.id || 'system',
        userName: updater?.name || currentUser?.name || 'System',
        userEmail: updater?.email || currentUser?.email || 'system@contractgroup.co.uk',
        metadata: {
          projectId: updated.id,
          projectName: updated.name,
          clientId: project.clientId,
          clientName: client?.name,
          previousValue: oldStage,
          newValue: newStage,
        },
      });
      
      // Send notifications to client users for ANY stage change (async, don't await to avoid blocking)
      this.sendProjectStageChangeNotifications(updated, client, oldStage, newStage).catch(err => 
        console.error('Failed to send stage change notifications:', err)
      );

      // The projects table trigger now commits the internal-review workflow
      // event atomically with the stage change. The browser only reflects the
      // result; email delivery no longer depends on this process staying open.
      
      // Send notifications when tender is submitted (async, don't await to avoid blocking)
      const newStageValue = updates.stage || (updates as any).project_stage;
      if (newStageValue === 'tender_submitted' || newStageValue === 'submitted') {
        this.sendTenderSubmittedNotifications(updated, client).catch(err => 
          console.error('Failed to send tender notifications:', err)
        );
      }
      
      // If project is moving away from 'enquiry_received' stage, update the associated task
      const oldStageValue = project.stage || (project as any).project_stage;
      if (oldStageValue === 'enquiry_received' && newStageValue !== 'enquiry_received') {
        const reviewTask = this.getTasks().find(
          (t: Task) => t.project_id === id && t.title.includes('Review New Enquiry')
        );
        
        if (reviewTask && reviewTask.status === 'pending') {
          console.log(`📋 Project moved from enquiry_received to ${newStageValue}, updating task "${reviewTask.title}" to in_progress`);
          this.updateTask(reviewTask.id, { status: 'in_progress' });
        }
      }
    }
    
    if (updates.status && updates.status !== project.status) {
      const updater = updates.createdBy ? this.getUser(updates.createdBy) : null;
      const currentUser = this.getUsers().find((u: User) => u.email === 'gary@contractgroup.co.uk');
      const client = this.getCompany(project.clientId);

      this.logActivity({
        type: 'project_status_changed',
        category: 'project',
        action: 'Project status updated',
        detail: `${updated.name}: ${project.status} → ${updates.status}`,
        userId: updater?.id || currentUser?.id || 'system',
        userName: updater?.name || currentUser?.name || 'System',
        userEmail: updater?.email || currentUser?.email || 'system@contractgroup.co.uk',
        metadata: {
          projectId: updated.id,
          projectName: updated.name,
          clientId: project.clientId,
          clientName: client?.name,
          previousValue: project.status,
          newValue: updates.status,
        },
      });

    }
    
    // Log contract value changes
    if (updates.contractValue !== undefined && updates.contractValue !== project.contractValue) {
      const updater = updates.createdBy ? this.getUser(updates.createdBy) : null;
      const currentUser = this.getUsers().find((u: User) => u.email === 'gary@contractgroup.co.uk');
      const difference = updates.contractValue - (project.contractValue || 0);
      
      this.logActivity({
        type: 'contract_value_change',
        related_to_type: 'project',
        related_to_id: updated.id,
        related_to_name: updated.name,
        title: 'Contract Value Updated',
        description: `Contract value changed from £${(project.contractValue || 0).toLocaleString()} to £${updates.contractValue.toLocaleString()} (${difference > 0 ? '+' : ''}£${Math.abs(difference).toLocaleString()})`,
        user_id: updater?.id || currentUser?.id || 'system',
        user_name: updater?.name || currentUser?.name || 'System',
        user_email: updater?.email || currentUser?.email || '',
        change_field: 'contractValue',
        change_old_value: (project.contractValue || 0).toString(),
        change_new_value: updates.contractValue.toString(),
        amount: difference,
        currency: 'GBP',
        is_internal: false,
      });
    }
    
    // Log archiving
    if ((updates as any).archive_status && (updates as any).archive_status !== (project as any).archive_status) {
      const updater = updates.createdBy ? this.getUser(updates.createdBy) : null;
      const currentUser = this.getUsers().find((u: User) => u.email === 'gary@contractgroup.co.uk');

      this.logActivity({
        type: 'status_update',
        related_to_type: 'project',
        related_to_id: updated.id,
        related_to_name: updated.name,
        title: (updates as any).archive_status === 'archived' ? 'Project Archived' : 'Project Restored',
        description: (updates as any).archive_status === 'archived'
          ? `Project archived. Reason: ${(updates as any).archive_reason || 'Not specified'}`
          : `Project restored from archive`,
        user_id: updater?.id || currentUser?.id || 'system',
        user_name: updater?.name || currentUser?.name || 'System',
        user_email: updater?.email || currentUser?.email || '',
        is_internal: true,
      });

      // Project-archived automation is enqueued atomically by the database.
    }
    
    // Log main contractor changes
    if (updates.mainContractorId && updates.mainContractorId !== project.mainContractorId) {
      const updater = updates.createdBy ? this.getUser(updates.createdBy) : null;
      const currentUser = this.getUsers().find((u: User) => u.email === 'gary@contractgroup.co.uk');
      const newContractor = this.getMainContractor(updates.mainContractorId);
      const oldContractor = project.mainContractorId ? this.getMainContractor(project.mainContractorId) : null;
      
      this.logActivity({
        type: 'note',
        related_to_type: 'project',
        related_to_id: updated.id,
        related_to_name: updated.name,
        title: 'Main Contractor Updated',
        description: oldContractor 
          ? `Main contractor changed from ${oldContractor.name} to ${newContractor?.name || 'Unknown'}`
          : `Main contractor set to ${newContractor?.name || 'Unknown'}`,
        user_id: updater?.id || currentUser?.id || 'system',
        user_name: updater?.name || currentUser?.name || 'System',
        user_email: updater?.email || currentUser?.email || '',
        is_internal: false,
      });
    }
    
    // Log practical completion date changes
    if (updates.practicalCompletionDate && updates.practicalCompletionDate !== project.practicalCompletionDate) {
      const updater = updates.createdBy ? this.getUser(updates.createdBy) : null;
      const currentUser = this.getUsers().find((u: User) => u.email === 'gary@contractgroup.co.uk');
      
      this.logActivity({
        type: 'note',
        related_to_type: 'project',
        related_to_id: updated.id,
        related_to_name: updated.name,
        title: 'Practical Completion Date Updated',
        description: project.practicalCompletionDate 
          ? `Practical completion date changed from ${new Date(project.practicalCompletionDate).toLocaleDateString()} to ${new Date(updates.practicalCompletionDate).toLocaleDateString()}`
          : `Practical completion date set to ${new Date(updates.practicalCompletionDate).toLocaleDateString()}`,
        user_id: updater?.id || currentUser?.id || 'system',
        user_name: updater?.name || currentUser?.name || 'System',
        user_email: updater?.email || currentUser?.email || '',
        is_internal: false,
      });
    }
    
    // Note: notifyListeners() is now called earlier (after optimistic update)
    // to ensure UI updates immediately without waiting for backend
    return updated;
  }

  /** Awaitable stage move for one-tap actions. Persists first, then runs the
   *  same activity log and client notification updateProject runs for a stage
   *  change, so a failed write never announces anything. Refuses (rejects)
   *  when the stage is no longer `from`, because the caller's read may be stale. */
  public async moveProjectStageAsync(id: string, from: string, to: string, actor: { id: string; name: string; email: string }): Promise<Project> {
    const existing = this.db.projects[id] as Project | undefined;
    if (!existing) throw new Error('Project not found');
    const current = existing.stage || (existing as any).project_stage;
    if (current !== from) throw new Error('Project stage has changed');
    const updated = await this.updateProjectPatchAsync(id, { stage: to, projectStage: to, project_stage: to } as Partial<Project>);
    const client = this.getCompany(existing.clientId);
    this.logActivity({
      type: 'project_status_changed',
      category: 'project',
      action: 'Project stage updated',
      detail: `${updated.name}: ${from} → ${to}`,
      userId: actor.id,
      userName: actor.name,
      userEmail: actor.email,
      metadata: { projectId: updated.id, projectName: updated.name, clientId: existing.clientId, clientName: client?.name, previousValue: from, newValue: to },
    });
    this.sendProjectStageChangeNotifications(updated, client, from, to).catch(err =>
      console.error('Failed to send stage change notifications:', err)
    );
    return updated;
  }

  /** Sales progress moves on the board at once; it reverts, and this rejects,
   *  unless the returned row confirms the persisted status. */
  public async updateProjectPipelineStatusAsync(id: string, status: ProjectPipelineStatus): Promise<Project> {
    if (!isProjectPipelineStatus(status)) throw new Error('Invalid project pipeline status');
    const existing = this.db.projects[id] as Project | undefined;
    if (!existing) throw new Error('Project not found');
    // Both spellings, because hydration treats the raw alias as authoritative.
    const shown = { ...existing, pipelineStatus: status, pipeline_status: status };
    return optimisticEntityWrite(this as unknown as OptimisticCache, 'projects', id, shown, () => this.updateProjectPatchAsync(id, { pipelineStatus: status }, saved => {
      if (saved.id !== id || !Object.prototype.hasOwnProperty.call(saved, 'pipelineStatus') || saved.pipelineStatus !== status) {
        throw new Error('Project pipeline status was not saved');
      }
    }));
  }

  /** Optional validation runs against the persisted row before cache updates. */
  public async updateProjectPatchAsync(id: string, updates: Partial<Project>, validateSaved?: (saved: Partial<Project>) => void): Promise<Project> {
    const existing = this.db.projects[id] as Project | undefined;
    if (!existing) throw new Error('Project not found');
    const response = await fetch(`${API_BASE}/projects/${id}`, {
      method: 'PUT',
      headers: await getAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(updates),
    });
    if (!response.ok) throw new Error(`Project update failed (${response.status})`);
    const saved = await response.json() as Partial<Project>;
    validateSaved?.(saved);
    const updated: Project = {
      ...existing,
      ...updates,
      ...saved,
      // Hydration keeps the raw alias authoritative. Reconcile it with a
      // confirmed camelCase response so a cached reload cannot restore a
      // stale sales status from that alias.
      ...(Object.prototype.hasOwnProperty.call(saved, 'pipelineStatus') ? { pipeline_status: saved.pipelineStatus } : {}),
      stage: saved.projectStage
        ? this.convertStageToBackend(saved.projectStage as string) as Project['stage']
        : existing.stage,
    };
    this.db.projects[id] = updated;
    this.saveToLocalStorage();
    this.notifyListeners();
    return updated;
  }

  private async sendProjectStageChangeNotifications(
    project: Project, 
    client: Company | undefined, 
    previousStage: string, 
    newStage: string
  ): Promise<void> {
    // Get all client users associated with the client company
    const clientUsers = client
      ? this.getUsersByCompany(client.id).filter((u: User) => u.role === 'client' && u.status === 'active')
      : [];

    if (clientUsers.length === 0) {
      // No client users to notify
      return;
    }
    
    // Verify which users actually exist in the backend database
    const existingUserIds = new Set<string>();
    try {
      const token = await this.getAuthHeaderToken();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/make-server-a476273b/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const backendUsers = await response.json();
        backendUsers.forEach((u: any) => existingUserIds.add(u.id));
      }
    } catch (error) {
      console.error('Failed to verify users for notifications:', error);
      // If we can't verify, don't send notifications to avoid errors
      return;
    }
    
    // Create human-readable stage labels
    const formatStageName = (stage: string): string => {
      const stageLabels: Record<string, string> = {
        'new_enquiry': 'New Enquiry',
        'inquiry_received': 'Inquiry Received',
        'awaiting_site_visit': 'Awaiting Site Visit',
        'awaiting_tender_documents': 'Awaiting Tender Documents',
        'tender_in_progress': 'Tender In Progress',
        'tender_submitted': 'Tender Submitted',
        'awaiting_result': 'Awaiting Result',
        'contract_awarded': 'Contract Awarded',
        'lost': 'Lost',
        'live': 'Live',
        'on_hold': 'On Hold',
        'completed': 'Completed',
        'archived': 'Archived',
      };
      return stageLabels[stage] || stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    };
    
    // Create notifications only for client users that exist in the backend
    clientUsers.forEach((user: User) => {
      // Skip users with invalid IDs or users that don't exist in the backend database
      if (!user.id || !existingUserIds.has(user.id)) {
        return;
      }

      this.createNotification({
        userId: user.id,
        type: 'progress_update',
        priority: 'medium',
        read: false,
        title: 'Project Stage Updated',
        message: `${project.name} has moved from \"${formatStageName(previousStage)}\" to \"${formatStageName(newStage)}\".`,
        projectId: project.id,
        projectName: project.name,
        relatedEntityType: 'project',
        relatedEntityId: project.id,
        actionUrl: buildProjectPathById(project.id),
        actionLabel: 'View Project',
        metadata: {
          projectId: project.id,
          projectName: project.name,
          clientId: project.clientId,
          clientName: client?.name,
          previousStage,
          newStage,
        },
      });
    });
  }

  private async sendTenderSubmittedNotifications(project: Project, client: Company | undefined): Promise<void> {
    // Get all staff users (admin, supervisor, staff roles)
    const staffUsers = this.getUsers().filter((u: User) =>
      ['admin', 'supervisor', 'staff'].includes(u.role) && u.status === 'active'
    );

    // Get all client users associated with the client company
    const clientUsers = client
      ? this.getUsersByCompany(client.id).filter((u: User) => u.role === 'client' && u.status === 'active')
      : [];
    
    // Tender submission is a client-facing event. Staff can verify it in the
    // project activity and status itself, so only notify client users here.
    const recipientUsers = clientUsers;
    
    // Verify which users actually exist in the backend database
    const existingUserIds = new Set<string>();
    try {
      const token = await this.getAuthHeaderToken();
      const response = await fetch(`${SUPABASE_URL}/functions/v1/make-server-a476273b/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const backendUsers = await response.json();
        backendUsers.forEach((u: any) => existingUserIds.add(u.id));
      }
    } catch (error) {
      console.error('Failed to verify users for notifications:', error);
      // If we can't verify, don't send notifications to avoid errors
      return;
    }
    
    // Create notifications only for users that exist in the backend
    recipientUsers.forEach((user: User) => {
      // Skip users with invalid IDs or users that don't exist in the backend database
      if (!user.id || !existingUserIds.has(user.id)) {
        return;
      }
      
      this.createNotification({
        userId: user.id,
        type: 'system',
        priority: 'high',
        read: false,
        title: 'Tender Submitted',
        message: client?.name
          ? `The tender for ${project.name} has been submitted. Client: ${client.name}.`
          : `The tender for ${project.name} has been submitted.`,
        projectId: project.id,
        projectName: project.name,
        relatedEntityType: 'project',
        relatedEntityId: project.id,
        actionUrl: buildProjectPathById(project.id),
        actionLabel: 'View Project',
        metadata: {
          projectId: project.id,
          projectName: project.name,
          clientId: project.clientId,
          clientName: client?.name,
          stage: 'tender_submitted',
        },
      });
    });
  }

  public deleteProject(id: string): boolean {
    if (!this.db.projects[id]) return false;
    delete this.db.projects[id];
    this.saveToLocalStorage();
    this.deleteEntityFromBackend('projects', id);
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // CONTRACT ITEMS
  // ============================================================================

  public getContractItems(): ContractItem[] {
    return Object.values(this.db.contractItems);
  }

  public getContractItem(id: string): ContractItem | undefined {
    return this.db.contractItems[id];
  }

  public getContractItemsByProject(projectId: string): ContractItem[] {
    return this.getContractItems().filter(item => item.projectId === projectId);
  }

  public getContractItemsByType(projectId: string, type: string): ContractItem[] {
    return this.getContractItems().filter(
      item => item.projectId === projectId && item.type === type
    );
  }

  public createContractItem(item: Omit<ContractItem, 'id' | 'createdAt' | 'updatedAt'>): ContractItem {
    const newItem: ContractItem = {
      ...item,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.db.contractItems[newItem.id] = newItem;
    this.saveToStorage();
    
    // Log activity for contract item creation
    const project = this.getProject(newItem.projectId);
    if (project && item.raisedBy) {
      const creator = this.getUser(item.raisedBy);
      const itemTypeLabel = newItem.type.toUpperCase();
      
      this.logActivity({
        type: 'contract_item',
        related_to_type: 'project',
        related_to_id: project.id,
        related_to_name: project.name,
        content: `New ${itemTypeLabel} "${newItem.reference || newItem.title}" created. Status: ${newItem.status}.`,
        subject: `${itemTypeLabel} Created: ${newItem.reference || newItem.title}`,
        user_id: creator?.id || item.raisedBy,
        user_name: creator?.name || 'Unknown User',
        user_email: creator?.email || '',
        is_internal: true,
      });
    }
    
    this.notifyListeners();
    return newItem;
  }

  public updateContractItem(id: string, updates: Partial<ContractItem>): ContractItem | undefined {
    const item = this.db.contractItems[id];
    if (!item) return undefined;
    
    const updated = {
      ...item,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.contractItems[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteContractItem(id: string): boolean {
    if (!this.db.contractItems[id]) return false;
    delete this.db.contractItems[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // Convenience methods for specific contract item types
  public getVariations(): ContractItem[] {
    return this.getContractItems().filter(item => item.type === 'variation');
  }

  public getCVIs(): ContractItem[] {
    return this.getContractItems().filter(item => item.type === 'cvi');
  }

  public getRFIs(): ContractItem[] {
    return this.getContractItems().filter(item => item.type === 'rfi');
  }

  public getEOTs(): ContractItem[] {
    return this.getContractItems().filter(item => item.type === 'eot');
  }

  // ============================================================================

  // INTERIM APPLICATIONS
  // ============================================================================

  public getInterimApplications(): InterimApplication[] {
    return Object.values(this.db.interimApplications);
  }

  public getInterimApplication(id: string): InterimApplication | undefined {
    return this.db.interimApplications[id];
  }

  public getInterimApplicationsByProject(projectId: string): InterimApplication[] {
    return this.getInterimApplications()
      .filter(app => app.projectId === projectId)
      .sort((a, b) => b.applicationNumber - a.applicationNumber);
  }

  /**
   * Inject a server-created interim application (with its real server-assigned id)
   * into the local cache so the record appears in the list immediately without
   * waiting for a full refreshFromBackend round-trip. The record must be in the
   * camelCase format that objectToCamelCase produces (same shape as the /database
   * endpoint returns via arrayToObject).
   */
  public upsertInterimApplicationFromServer(app: InterimApplication): void {
    this.db.interimApplications[app.id] = app;
    this.saveToStorage();
    this.notifyListeners();
  }

  public createInterimApplication(app: Omit<InterimApplication, 'id' | 'createdAt' | 'updatedAt'>): InterimApplication {
    // Invoice numbers are allocated atomically by the server's
    // `allocate_workspace_document_number` RPC before the API POST in
    // `useInterimData.ts`. The reference is included in the request body
    // and round-trips back via `savedApp`. Local creation just persists
    // whatever the caller passes through.
    const newApp: InterimApplication = {
      ...app,
      id: this.generateId(),
      localOnly: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.db.interimApplications[newApp.id] = newApp;
    this.saveToStorage();
    
    // Log activity for interim application creation
    const project = this.getProject(newApp.projectId);
    const appAny = app as any;
    if (project && appAny.createdBy) {
      const creator = this.getUser(appAny.createdBy);

      this.logActivity({
        type: 'interim_application',
        related_to_type: 'project',
        related_to_id: project.id,
        related_to_name: project.name,
        content: `Interim Application #${newApp.applicationNumber} submitted. Gross value: £${(newApp.grossValueClaimed || 0).toLocaleString('en-GB')}. Status: ${newApp.status}.`,
        subject: `Payment Application #${newApp.applicationNumber} Submitted`,
        user_id: creator?.id || appAny.createdBy,
        user_name: creator?.name || 'Unknown User',
        user_email: creator?.email || '',
        amount: newApp.grossValueClaimed,
        currency: 'GBP',
        is_internal: true,
      });
    }
    
    this.notifyListeners();
    return newApp;
  }

  public updateInterimApplication(id: string, updates: Partial<InterimApplication>): InterimApplication | undefined {
    const app = this.db.interimApplications[id];
    if (!app) return undefined;

    // Invoice-number allocation lives in the API save flow
    // (`useInterimData.ts`), not here — it must be atomic against the
    // workspace counter and only the server endpoint can guarantee that.
    const updated: InterimApplication = {
      ...app,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.interimApplications[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteInterimApplication(id: string): boolean {
    if (!this.db.interimApplications[id]) return false;
    delete this.db.interimApplications[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================

  // TENDERS
  // ============================================================================

  public getTenders(): Tender[] {
    // Tenders are now just projects in tender stages
    const tenderStages = [
      'inquiry_received',
      'pricing_in_progress', 
      'quote_submitted',
      'quote_pending_review',
      'quote_accepted',
      'quote_rejected',
      'contract_awarded'
    ];
    
    const projects = Object.values(this.db.projects || {}) as Project[];
    return projects.filter((p: Project) => tenderStages.includes(p.stage || '')) as any[];
  }

  public getTender(id: string): Tender | undefined {
    const project = this.db.projects?.[id];
    if (!project) return undefined;
    
    const tenderStages = [
      'inquiry_received',
      'pricing_in_progress', 
      'quote_submitted',
      'quote_pending_review',
      'quote_accepted',
      'quote_rejected',
      'contract_awarded'
    ];
    
    return tenderStages.includes(project.stage || '') ? project as any : undefined;
  }

  public getTendersByClient(clientId: string): Tender[] {
    return this.getTenders().filter(t => t.clientId === clientId);
  }

  public getTendersByStatus(status: string): Tender[] {
    return this.getTenders().filter(t => (t as any).status === status);
  }

  public createTender(tender: Omit<Tender, 'id' | 'createdAt' | 'updatedAt'>): Tender {
    // Create as a project in tender stage
    const newProject = this.createProject({
      ...tender as any,
      stage: tender.stage || 'inquiry_received',
    });
    return newProject as any;
  }

  public updateTender(id: string, updates: Partial<Tender>): Tender | undefined {
    // Update the underlying project
    const updatedProject = this.updateProject(id, updates as any);
    return updatedProject as any;
  }

  public deleteTender(id: string): boolean {
    // Delete the underlying project
    return this.deleteProject(id);
  }

  // ============================================================================

  // RETENTION LEDGERS
  // ============================================================================

  public getRetentionLedgers(): RetentionLedger[] {
    return Object.values(this.db.retentionLedgers);
  }

  public getRetentionLedger(id: string): RetentionLedger | undefined {
    return this.db.retentionLedgers[id];
  }

  public getRetentionLedgerByProject(projectId: string): RetentionLedger | undefined {
    return this.getRetentionLedgers().find(ledger => ledger.projectId === projectId);
  }

  public createRetentionLedger(ledger: Omit<RetentionLedger, 'id' | 'createdAt' | 'updatedAt'>): RetentionLedger {
    const newLedger: RetentionLedger = {
      ...ledger,
      id: this.generateId(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.db.retentionLedgers[newLedger.id] = newLedger;
    this.saveToStorage();
    this.notifyListeners();
    return newLedger;
  }

  public updateRetentionLedger(id: string, updates: Partial<RetentionLedger>): RetentionLedger | undefined {
    const ledger = this.db.retentionLedgers[id];
    if (!ledger) return undefined;
    
    const updated = {
      ...ledger,
      ...updates,
      id,
      updatedAt: new Date().toISOString(),
    };
    this.db.retentionLedgers[id] = updated;
    this.saveToStorage();
    this.notifyListeners();
    return updated;
  }

  public deleteRetentionLedger(id: string): boolean {
    if (!this.db.retentionLedgers[id]) return false;
    delete this.db.retentionLedgers[id];
    this.saveToStorage();
    this.notifyListeners();
    return true;
  }

  // ============================================================================
};
